import {
  TypeProduct_cardWithoutUnresolvableLinksResponse,
  TypeProgram_metaWithoutUnresolvableLinksResponse
} from "@2u-landing-pages/types";

type Location = {
  latitude: string;
  longitude: string;
}

function getCookie(name: string): string {
  if (typeof window !== "undefined") {
    if (window?.document?.cookie) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);

      if (parts?.length === 2) {
        return parts?.pop()?.split(";")[0] || '';
      }
    }
  }

  return "";
};

const DEG_TO_RAD = 0.017453292519943295; // Math.PI / 180
const EARTH_RADIUS_IN_MILES = 3958.56540656;

function deg2rad(value: number): number {
  return value * DEG_TO_RAD;
};

export function getDistanceInMiles(locationA: Location, locationB: Location): number {
  const locationALatitudeRadians = deg2rad(Number(locationA.latitude));
  const locationBLatitudeRadians = deg2rad(Number(locationB.latitude));

  const distance = Math.sin(locationALatitudeRadians)
    * Math.sin(locationBLatitudeRadians)
    + Math.cos(locationALatitudeRadians)
    * Math.cos(locationBLatitudeRadians)
    * Math.cos(deg2rad(Number(locationA.longitude) - Number(locationB.longitude)));

  return Math.round(Math.acos(distance) * EARTH_RADIUS_IN_MILES);
}

export function getProgramList(
  programs: TypeProduct_cardWithoutUnresolvableLinksResponse[],
  filters: any,
  sort: string = "proximity:asc",
  customProgramOrder: string[],
  locationConsent: boolean,
  geo_sort: boolean = true
) {
  if (!programs?.length) {
    return [];
  }

  if (customProgramOrder.length) {
    const filteredPrograms = filterPrograms(programs, filters);

    const customOrderedPrograms = customProgramOrder.map((id) => {
      return filteredPrograms.find((program) => program.sys.id === id);
    });

    return customOrderedPrograms.filter((program) => program !== undefined);
  }

  if (sort.includes("proximity")) {
    // If we do not have location consent or don't have location cookies, don't sort by proximity
    if (!locationConsent || !getCookie("x-u-lat") || !getCookie("x-u-lon") || !geo_sort) {
      return filterPrograms(programs, filters);
    }
  }

  return filterAndSortPrograms(programs, filters, sort, locationConsent);
}

export function filterPrograms(programs: TypeProduct_cardWithoutUnresolvableLinksResponse[], filters: any) {
  return programs?.filter((program) => {
    let keep = true;
    const { fields } = program?.fields?.program_meta || {};
    const keys = Object.keys(filters);
    let i = 0;

    while (i < keys.length && keep) {
      const key = keys[i];
      const value = filters[key];
      const field =
        key !== "start_date"
          ? fields?.[key as keyof TypeProgram_metaWithoutUnresolvableLinksResponse["fields"]]
          : formatDate(fields?.start_date as string);

      if (value && !value.includes(field)) {
        keep = false;
      }

      i++;
    }

    return keep;
  });
}

export function filterAndSortPrograms(
  programs: TypeProduct_cardWithoutUnresolvableLinksResponse[],
  filters: any,
  sort: string = "proximity:asc",
  locationConsent: boolean
) {
  const filteredPrograms = filterPrograms(programs, filters);

  const sortedPrograms = filteredPrograms?.sort(
    (a: TypeProduct_cardWithoutUnresolvableLinksResponse, b: TypeProduct_cardWithoutUnresolvableLinksResponse) => {
      const [sortKey, sortOrder] = sort.split(":");
      let aValue: any =
        a.fields.program_meta?.fields[sortKey as keyof TypeProgram_metaWithoutUnresolvableLinksResponse["fields"]];
      let bValue: any =
        b.fields.program_meta?.fields[sortKey as keyof TypeProgram_metaWithoutUnresolvableLinksResponse["fields"]];

      if (sortKey === "start_date") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortKey === "tuition_cost") {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      } else if (sortKey === "proximity") {
        const userLat = getCookie("x-u-lat");
        const userLon = getCookie("x-u-lon");

        aValue = getDistanceInMiles(
          {
            latitude: a.fields.program_meta?.fields.latitude || "",
            longitude: a.fields.program_meta?.fields.longitude || "",
          },
          {
            latitude: userLat,
            longitude: userLon,
          }
        );

        bValue = getDistanceInMiles(
          {
            latitude: b.fields.program_meta?.fields.latitude || "",
            longitude: b.fields.program_meta?.fields.longitude || "",
          },
          {
            latitude: userLat,
            longitude: userLon,
          }
        );
      }

      if (sortOrder === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    }
  );

  return sortedPrograms;
}

export function formatDate(date: string) {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
  });
}

export function getFilterLabels(props: Record<string, unknown>) {
  const filterLabels = Object.entries(props).reduce((acc, [key, value]) => {
    if (key.startsWith('filter_label_') && typeof value === 'string') {
      const cleanKey = key.replace('filter_label_', '');
      acc[cleanKey] = value;
    }
    return acc;
  }, {} as Record<string, string>);

  return filterLabels;
};
